// LegalMarkdown — v242.6.0
// Single source of truth for rendering admin-managed legal content (Terms / Privacy).
//
// Why this exists: the admin panel (LegalContentPage) stores legal copy as MARKDOWN
// (# / ## / ### / - / **bold**) in app_config.legal_content. Previously the patient app
// rendered legal text three different ways (hardcoded settings screens + a plain-text
// PolicyModal parser), so admin edits never showed up consistently. This module gives all
// three surfaces one fetch + one renderer, with the live DB content as the source of truth
// and bundled copy demoted to offline-only fallback.
//
// ★★ 4.39 (P3, the default for Q-CR1) removed every bundled copy (PrivacyPolicyScreen's
//   FallbackPrivacy, PolicyModal's PRIVACY_CONTENT and TERMS_CONTENT).
// ★★ PROD-763 (Dr. Rami list-2 S11, R31 amended): the database text is shown; when it cannot be
//   loaded (a failure, no text, or no answer within LEGAL_LOAD_TIMEOUT_MS) the PRIVACY text falls
//   back to the ONE bundled copy (utils/bundledPrivacy.js), and the hook reports source
//   'database' | 'bundled' and version so the consent can record which text was shown. The terms
//   keep the 4.39 behaviour: <LegalLoadFailed> says so in one sentence, offers "Try again"
//   (useLegalContent's reload) and links the published page on the website.
//   Gates: scripts/check-legal-one-source.mjs, scripts/check-privacy-fallback.mjs.

import { useState, useEffect, useCallback } from 'react'
import { Capacitor } from '@capacitor/core'
import { useI18n } from '../i18n'
import { loadLegalDocument } from '../utils/legalDocument'

const NAVY = '#15354F'
const API_BASE = import.meta.env.VITE_API_URL || 'https://api.orellahealth.com/api/v1'

/**
 * useLegalContent — the database text of one legal document, else (privacy only) the bundled copy.
 * @param {'terms'|'privacy'} type
 * @returns {{ content: string, loading: boolean, failed: boolean, reload: () => void,
 *   source: 'database'|'bundled'|null, version: string|null }}
 *   failed = loaded and still no text (terms only, since the privacy text falls back to the bundle;
 *   the caller shows <LegalLoadFailed>).
 */
export function useLegalContent(type) {
  const [doc, setDoc] = useState({ content: '', source: null, version: null })
  const [loading, setLoading] = useState(true)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    loadLegalDocument(type, { url: `${API_BASE}/legal-content`, fetchImpl: (...args) => fetch(...args) }).then((next) => {
      if (!cancelled) { setDoc(next); setLoading(false) }
    })
    return () => { cancelled = true }
  }, [type, attempt])

  const reload = useCallback(() => setAttempt((n) => n + 1), [])
  return { content: doc.content, loading, failed: !loading && !doc.content, reload, source: doc.source, version: doc.version }
}

// 4.39: the published copies on the website (measured 2026-10-06: /legal/privacy and
// /legal/terms answer 200; /privacy answers 404).
export const LEGAL_WEB_URLS = {
  privacy: 'https://orellahealth.com/legal/privacy',
  terms: 'https://orellahealth.com/legal/terms',
}

/** 4.39 — the text could not be loaded: one sentence, "Try again", and the website's copy. */
export function LegalLoadFailed({ type, onRetry }) {
  const { t } = useI18n()
  const url = LEGAL_WEB_URLS[type === 'terms' ? 'terms' : 'privacy']
  const openOnNative = (e) => {
    if (!Capacitor.isNativePlatform()) return
    e.preventDefault()
    import('@capacitor/browser').then(({ Browser }) => Browser.open({ url })).catch(() => {})
  }
  return (
    <div className="text-center" style={{ padding: '36px 8px' }} data-legal-load-failed={type}>
      <p style={{ fontSize: 13, color: NAVY, lineHeight: 1.5, marginBottom: 14 }}>{t('legal.loadFailed')}</p>
      <button
        type="button"
        onClick={onRetry}
        className="font-semibold"
        style={{ fontSize: 13, padding: '9px 18px', borderRadius: 12, border: 'none', background: '#E9F8FF', color: NAVY, cursor: 'pointer' }}
      >
        {t('common.tryAgain')}
      </button>
      <p style={{ marginTop: 14 }}>
        <a href={url} target="_blank" rel="noopener noreferrer" onClick={openOnNative} style={{ fontSize: 12.5, color: '#46A8F9', fontWeight: 600 }}>
          {t('legal.readOnWebsite')}
        </a>
      </p>
    </div>
  )
}

// Inline parser: splits a line on **bold** spans and returns React nodes.
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} style={{ fontWeight: 700, color: NAVY }}>{part.slice(2, -2)}</strong>
    }
    return <span key={i}>{part}</span>
  })
}

/**
 * LegalMarkdown — renders the admin markdown conventions into branded document styling.
 * Supports: # H1, ## H2 (section), ### H3, - / * bullets, **bold**, blank-line paragraphs,
 * and a muted "Last updated" / "Effective Date" subtitle line.
 *
 * @param {string}  text       markdown source
 * @param {boolean} hideTitle  drop a leading "# ..." line (the surrounding chrome already
 *                             shows the document title, avoiding a duplicate heading)
 */
export default function LegalMarkdown({ text, hideTitle = false }) {
  if (!text) return null
  const lines = String(text).replace(/\r\n/g, '\n').split('\n')
  const out = []
  let bullets = []
  let titleDropped = false

  const flushBullets = () => {
    if (!bullets.length) return
    out.push(
      <ul key={`ul-${out.length}`} style={{ margin: '4px 0 10px', paddingLeft: 18 }}>
        {bullets.map((b, i) => (
          <li key={i} style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.6, marginBottom: 4 }}>
            {renderInline(b)}
          </li>
        ))}
      </ul>
    )
    bullets = []
  }

  lines.forEach((raw, idx) => {
    const line = raw.trim()

    if (!line) { flushBullets(); out.push(<div key={`sp-${idx}`} style={{ height: 8 }} />); return }

    // Bullets (- or *)
    if (/^[-*]\s+/.test(line)) { bullets.push(line.replace(/^[-*]\s+/, '')); return }
    flushBullets()

    // Headings
    if (line.startsWith('### ')) {
      out.push(<p key={idx} style={{ fontSize: 12.5, fontWeight: 700, color: NAVY, marginTop: 12, marginBottom: 4 }}>{renderInline(line.slice(4))}</p>)
      return
    }
    if (line.startsWith('## ')) {
      out.push(
        <p key={idx} style={{
          fontSize: 12.5, fontWeight: 700, color: NAVY, marginTop: 16, marginBottom: 8,
          paddingBottom: 6, borderBottom: `1.5px solid ${NAVY}`,
        }}>{renderInline(line.slice(3))}</p>
      )
      return
    }
    if (line.startsWith('# ')) {
      if (hideTitle && !titleDropped) { titleDropped = true; return }
      out.push(<p key={idx} style={{ fontSize: 18, fontWeight: 700, color: NAVY, marginBottom: 4 }}>{renderInline(line.slice(2))}</p>)
      return
    }

    // Muted subtitle (date line)
    if (/^(last updated|effective date)/i.test(line)) {
      out.push(<p key={idx} style={{ fontSize: 10.5, color: '#94A3B8', marginBottom: 10 }}>{line}</p>)
      return
    }

    // Paragraph
    out.push(<p key={idx} style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.65, marginBottom: 8 }}>{renderInline(line)}</p>)
  })

  flushBullets()
  return <>{out}</>
}
